import { useState, useEffect } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';
import type { PromptContent } from '../services/api';

export function PromptEditorWidget({ widgetProps }: WidgetProps) {
  const { artifacts, activeArtifact, loadArtifacts, selectArtifact, saveVersion, promote } = useArtifactStore();
  const [selectedId, setSelectedId] = useState<string>(widgetProps?.artifactId as string || '');
  const [promptText, setPromptText] = useState('');
  const [toolBindings, setToolBindings] = useState<string[]>([]);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [model, setModel] = useState('claude-opus-4-7');
  const [temperature, setTemperature] = useState(0.7);

  useEffect(() => { loadArtifacts('prompt'); }, []);

  useEffect(() => {
    if (selectedId) selectArtifact(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (!activeArtifact || activeArtifact.content.type !== 'prompt') return;
    const c = activeArtifact.content as PromptContent;
    setPromptText(c.systemPrompt);
    setToolBindings(c.toolBindings);
    setModel(c.model);
    setTemperature(c.temperature);
    const vars: Record<string, string> = {};
    c.variables.forEach(v => { vars[v.name] = v.defaultValue || ''; });
    setVariables(vars);
  }, [activeArtifact?.id]);

  const extractedVars = promptText.match(/\{\{(\w+)\}\}/g)?.map(m => m.slice(2, -2)) || [];

  const renderedPreview = promptText.replace(/\{\{(\w+)\}\}/g, (_m, key) => {
    return variables[key] || `[${key}]`;
  });

  const handleSave = () => {
    if (!activeArtifact) return;
    const note = prompt('Change note:');
    if (!note) return;
    const content: PromptContent = {
      type: 'prompt',
      systemPrompt: promptText,
      toolBindings,
      model,
      temperature,
      maxTokens: (activeArtifact.content as PromptContent).maxTokens || 4096,
      variables: extractedVars.map(name => ({
        name,
        description: '',
        defaultValue: variables[name] || '',
        required: false,
      })),
      agentId: (activeArtifact.content as PromptContent).agentId,
    };
    saveVersion(activeArtifact.artifactId, content, note);
  };

  const handlePromote = () => {
    if (!activeArtifact) return;
    if (confirm(`Promote prompt v${activeArtifact.version} to live?`)) {
      promote(activeArtifact.artifactId, activeArtifact.id);
    }
  };

  const promptArtifacts = artifacts.filter(a => a.content.type === 'prompt');

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-2 py-1 border-b border-tower-border/50 shrink-0">
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
          className="bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1 py-0.5 max-w-[180px]">
          <option value="">Select prompt...</option>
          {promptArtifacts.map(a => (
            <option key={a.artifactId} value={a.artifactId}>{a.name} (v{a.version})</option>
          ))}
        </select>
        <select value={model} onChange={(e) => setModel(e.target.value)}
          className="bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1 py-0.5">
          <option value="claude-opus-4-7">Opus 4.7</option>
          <option value="claude-sonnet-4-6">Sonnet 4.6</option>
          <option value="claude-haiku-4-5">Haiku 4.5</option>
        </select>
        <input type="number" value={temperature} onChange={(e) => setTemperature(Number(e.target.value))}
          min={0} max={2} step={0.1}
          className="bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1 py-0.5 w-14"
          title="Temperature" />
        <button onClick={handleSave} disabled={!activeArtifact}
          className="text-xs px-2 py-0.5 bg-primary hover:bg-primary disabled:bg-tower-border text-textOnPrimary rounded">Save</button>
        {activeArtifact && !activeArtifact.isLive && (
          <button onClick={handlePromote}
            className="text-xs px-2 py-0.5 bg-warning hover:bg-warning text-textOnPrimary rounded">Promote</button>
        )}
        {activeArtifact && (
          <span className="ml-auto text-xs text-tower-muted">
            <span className={`w-2 h-2 rounded-full inline-block mr-1 ${activeArtifact.isLive ? 'bg-success' : 'bg-warning'}`} />
            v{activeArtifact.version}
          </span>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col">
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="System prompt with {{variable}} placeholders..."
            className="flex-1 bg-tower-bg text-tower-text text-xs font-mono p-3 resize-none focus:outline-none border-b border-tower-border/30"
            spellCheck={false}
          />
          <div className="h-1/3 overflow-y-auto p-2 bg-tower-border/10">
            <div className="text-[10px] text-tower-muted font-semibold mb-1">RENDERED PREVIEW</div>
            <div className="text-xs text-tower-text whitespace-pre-wrap font-mono">{renderedPreview}</div>
          </div>
        </div>

        <div className="w-44 border-l border-tower-border/50 p-2 overflow-y-auto shrink-0">
          {extractedVars.length > 0 && (
            <>
              <div className="text-xs text-tower-muted font-semibold mb-2">Variables</div>
              {extractedVars.map(v => (
                <div key={v} className="mb-2">
                  <label className="text-[10px] text-tower-muted block">{`{{${v}}}`}</label>
                  <input
                    value={variables[v] || ''}
                    onChange={(e) => setVariables(prev => ({ ...prev, [v]: e.target.value }))}
                    className="w-full bg-tower-bg border border-tower-border rounded px-1 py-0.5 text-xs text-tower-text"
                  />
                </div>
              ))}
            </>
          )}
          <div className="text-xs text-tower-muted font-semibold mt-3 mb-1">Tool Bindings</div>
          <textarea
            value={toolBindings.join('\n')}
            onChange={(e) => setToolBindings(e.target.value.split('\n').filter(Boolean))}
            placeholder="One tool name per line"
            className="w-full bg-tower-bg border border-tower-border rounded px-1 py-0.5 text-xs text-tower-text h-20 resize-none font-mono"
          />
        </div>
      </div>
    </div>
  );
}
